import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlertIcon, EyeIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ActivityIcon, TrendingUpIcon, UserXIcon } from 'lucide-react';
import { MOCK_FRAUD_ALERTS } from '../../constants/data';
import { formatDate } from '../../utils/dateUtils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Table } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { MetricGrid } from '../../components/features/MetricCard';
const priorityTabs = [{
  id: 'all',
  label: 'All Alerts',
  count: 15
}, {
  id: 'high',
  label: 'High Priority',
  count: 3
}, {
  id: 'medium',
  label: 'Medium',
  count: 7
}, {
  id: 'low',
  label: 'Low',
  count: 5
}];
// Priority indicator component
function PriorityIndicator({
  priority
}: {
  priority: 'high' | 'medium' | 'low';
}) {
  const colors = {
    high: 'bg-error-500',
    medium: 'bg-warning-500',
    low: 'bg-primary-500'
  };
  return <div className="flex items-center gap-2" data-id="element-678">
      <motion.div className={`w-2.5 h-2.5 rounded-full ${colors[priority]}`} animate={priority === 'high' ? {
      scale: [1, 1.3, 1],
      opacity: [1, 0.7, 1]
    } : {}} transition={{
      duration: 1.5,
      repeat: Infinity
    }} data-id="element-679" />
      <span className={`text-body-sm font-medium capitalize ${priority === 'high' ? 'text-error-600' : priority === 'medium' ? 'text-warning-600' : 'text-primary-600'}`} data-id="element-680">
        {priority}
      </span>
    </div>;
}
// Alert card for high priority alerts
function HighPriorityAlert({
  alert,
  onClick
}: {
  alert: (typeof MOCK_FRAUD_ALERTS)[0];
  onClick: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} whileHover={{
    scale: 1.02
  }} onClick={onClick} className="p-4 bg-gradient-to-r from-error-50 to-error-100/50 border border-error-200 rounded-xl cursor-pointer" data-id="element-681">
      <div className="flex items-start gap-4" data-id="element-682">
        <motion.div className="w-12 h-12 bg-error-100 rounded-xl flex items-center justify-center flex-shrink-0" animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-683">
          <AlertTriangleIcon className="w-6 h-6 text-error-600" data-id="element-684" />
        </motion.div>
        <div className="flex-1 min-w-0" data-id="element-685">
          <div className="flex items-center gap-2 mb-1" data-id="element-686">
            <span className="text-body-sm font-bold text-error-700" data-id="element-687">
              {alert.id}
            </span>
            <span className="px-2 py-0.5 bg-error-200 text-error-700 text-caption font-semibold rounded-full" data-id="element-688">
              HIGH PRIORITY
            </span>
          </div>
          <p className="text-body-md font-medium text-surface-900 mb-1" data-id="element-689">
            {alert.description}
          </p>
          <p className="text-body-sm text-surface-600" data-id="element-690">
            {formatDate(alert.createdAt, {
            includeTime: true
          })}
          </p>
        </div>
        <Button variant="ghost" size="sm" icon={<EyeIcon className="w-4 h-4" data-id="element-692" />} data-id="element-691">
          View
        </Button>
      </div>
    </motion.div>;
}
export function FraudDetection() {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState<(typeof MOCK_FRAUD_ALERTS)[0] | null>(null);
  const metrics = [{
    label: 'Open Alerts',
    value: '8',
    change: {
      value: 2,
      trend: 'up' as const
    },
    icon: <ShieldAlertIcon className="w-5 h-5" data-id="element-693" />,
    iconColor: 'bg-error-100 text-error-600'
  }, {
    label: 'Investigating',
    value: '4',
    icon: <ActivityIcon className="w-5 h-5" data-id="element-694" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Resolved Today',
    value: '12',
    change: {
      value: 25,
      trend: 'up' as const
    },
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-695" />,
    iconColor: 'bg-success-100 text-success-600'
  }, {
    label: 'Blocked Users',
    value: '2',
    icon: <UserXIcon className="w-5 h-5" data-id="element-696" />,
    iconColor: 'bg-surface-100 text-surface-600'
  }];
  const columns = [{
    key: 'id',
    header: 'Alert ID'
  }, {
    key: 'priority',
    header: 'Priority',
    render: (value: unknown) => <PriorityIndicator priority={value as 'high' | 'medium' | 'low'} data-id="element-697" />
  }, {
    key: 'type',
    header: 'Type',
    render: (value: unknown) => <span className="px-2 py-1 bg-surface-100 text-surface-700 text-caption font-medium rounded-md capitalize" data-id="element-698">
          {(value as string).replace('_', ' ')}
        </span>
  }, {
    key: 'description',
    header: 'Description'
  }, {
    key: 'status',
    header: 'Status',
    render: (value: unknown) => {
      const status = value as string;
      return <StatusBadge status={status === 'resolved' ? 'complete' : status === 'investigating' ? 'processing' : 'pending'} size="sm" data-id="element-699">
            {status}
          </StatusBadge>;
    }
  }, {
    key: 'createdAt',
    header: 'Created',
    render: (value: unknown) => <span className="text-body-sm text-surface-600" data-id="element-700">
          {formatDate(value as string, {
        includeTime: true
      })}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (_: unknown, row: (typeof MOCK_FRAUD_ALERTS)[0]) => <motion.div whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} data-id="element-701">
          <Button variant="ghost" size="sm" icon={<EyeIcon className="w-4 h-4" data-id="element-703" />} onClick={() => setSelectedAlert(row)} data-id="element-702">
            View
          </Button>
        </motion.div>
  }];
  const highPriorityAlerts = MOCK_FRAUD_ALERTS.filter(a => a.priority === 'high' && a.status !== 'resolved');
  const filteredAlerts = MOCK_FRAUD_ALERTS.filter(alert => {
    if (activeTab === 'all') return true;
    return alert.priority === activeTab;
  });
  return <div className="space-y-6" data-id="element-704">
      {/* Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-705">
        <MetricGrid metrics={metrics} columns={4} data-id="element-706" />
      </motion.div>

      {/* High Priority Alerts */}
      {highPriorityAlerts.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-707">
          <Card variant="elevated" padding="md" className="shadow-lg border-0 border-l-4 border-l-error-500" data-id="element-708">
            <div className="flex items-center gap-2 mb-4" data-id="element-709">
              <motion.div animate={{
            rotate: [0, 10, -10, 0]
          }} transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatDelay: 2
          }} data-id="element-710">
                <AlertTriangleIcon className="w-5 h-5 text-error-500" data-id="element-711" />
              </motion.div>
              <h2 className="text-heading-md font-bold text-surface-900" data-id="element-712">
                High Priority Alerts ({highPriorityAlerts.length})
              </h2>
            </div>
            <div className="space-y-3" data-id="element-713">
              {highPriorityAlerts.map((alert, index) => <motion.div key={alert.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.1
          }} data-id="element-714">
                  <HighPriorityAlert alert={alert} onClick={() => setSelectedAlert(alert)} data-id="element-715" />
                </motion.div>)}
            </div>
          </Card>
        </motion.div>}

      {/* Alerts Table */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-716">
        <Card variant="elevated" padding="none" className="shadow-lg border-0 overflow-hidden" data-id="element-717">
          <div className="p-4 border-b border-surface-100" data-id="element-718">
            <Tabs tabs={priorityTabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-719" />
          </div>
          <Table columns={columns} data={filteredAlerts} keyExtractor={row => row.id} onRowClick={setSelectedAlert} data-id="element-720" />
        </Card>
      </motion.div>

      {/* Alert Detail Modal */}
      <Modal isOpen={!!selectedAlert} onClose={() => setSelectedAlert(null)} title="Alert Details" size="lg" data-id="element-721">
        {selectedAlert && <>
            <div className="space-y-6" data-id="element-722">
              {/* Priority Banner */}
              <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`p-4 rounded-xl ${selectedAlert.priority === 'high' ? 'bg-error-50 border border-error-200' : selectedAlert.priority === 'medium' ? 'bg-warning-50 border border-warning-200' : 'bg-primary-50 border border-primary-200'}`} data-id="element-723">
                <div className="flex items-center gap-4" data-id="element-724">
                  <motion.div animate={selectedAlert.priority === 'high' ? {
                scale: [1, 1.1, 1]
              } : {}} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-725">
                    <AlertTriangleIcon className={`w-8 h-8 ${selectedAlert.priority === 'high' ? 'text-error-500' : selectedAlert.priority === 'medium' ? 'text-warning-500' : 'text-primary-500'}`} data-id="element-726" />
                  </motion.div>
                  <div data-id="element-727">
                    <p className="text-body-md font-bold text-surface-900" data-id="element-728">
                      {selectedAlert.priority.charAt(0).toUpperCase() + selectedAlert.priority.slice(1)}{' '}
                      Priority Alert
                    </p>
                    <p className="text-body-sm text-surface-600" data-id="element-729">
                      {selectedAlert.description}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4" data-id="element-730">
                {[{
              label: 'Alert ID',
              value: selectedAlert.id
            }, {
              label: 'Type',
              value: selectedAlert.type.replace('_', ' ')
            }, {
              label: 'Status',
              value: selectedAlert.status
            }, {
              label: 'Created',
              value: formatDate(selectedAlert.createdAt, {
                includeTime: true
              })
            }].map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="p-3 bg-surface-50 rounded-lg" data-id="element-731">
                    <p className="text-body-sm text-surface-500 mb-1" data-id="element-732">
                      {item.label}
                    </p>
                    <p className="text-body-md font-semibold text-surface-900 capitalize" data-id="element-733">
                      {item.value}
                    </p>
                  </motion.div>)}
              </div>

              {/* Alert Details */}
              <div data-id="element-734">
                <h4 className="text-heading-sm font-semibold text-surface-900 mb-3" data-id="element-735">
                  Alert Details
                </h4>
                <div className="p-4 bg-surface-50 rounded-xl border border-surface-200" data-id="element-736">
                  <pre className="text-body-sm text-surface-700 whitespace-pre-wrap font-mono" data-id="element-737">
                    {JSON.stringify(selectedAlert.details, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            <ModalFooter data-id="element-738">
              <Button variant="secondary" onClick={() => setSelectedAlert(null)} data-id="element-739">
                Close
              </Button>
              {selectedAlert.status !== 'resolved' && <>
                  <Button variant="ghost" icon={<XCircleIcon className="w-4 h-4" data-id="element-741" />} data-id="element-740">
                    Dismiss
                  </Button>
                  <Button icon={<CheckCircleIcon className="w-4 h-4" data-id="element-743" />} data-id="element-742">
                    Mark Resolved
                  </Button>
                </>}
            </ModalFooter>
          </>}
      </Modal>
    </div>;
}